import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { LockKeyhole } from 'lucide-react';
import { useCapabilities } from '../../context/CapabilityContext';
import './BillingAccessRequired.css';

// Conditions that support resolves rather than a payment.
const reasons = {
    BILLING_REVIEW_REQUIRED: ['Åtkomsten är pausad för granskning', 'Kontakta support så hjälper vi dig att reda ut företagets åtkomst.'],
    BILLING_COMPANY_ALREADY_REGISTERED: ['Organisationsnumret är redan registrerat', 'Kontakta support för att reda ut registreringen och återställa företagets åtkomst.'],
    BILLING_IDENTITY_REQUIRED: ['Företagsuppgifterna behöver kontrolleras', 'Kontrollera företagets organisationsnummer i företagsuppgifterna.'],
    COMPANY_OFFBOARDING: ['Företaget håller på att avslutas', 'Åtkomsten är pausad medan företaget avslutas. Kontakta support om du behöver hjälp.'],
};

// The title names the situation and one sentence says what happens next,
// in the same words as the billing card in settings. Owners are told what
// to do; members are told who does it and when the company reopens.
function explain(entitlements, isOwner) {
    const code = entitlements.billingAccess?.code;
    if (reasons[code]) return { tone: 'danger', title: reasons[code][0], text: reasons[code][1] };
    if (['cancelled', 'canceled'].includes(entitlements.status)) return { tone: 'attention', title: 'Prenumerationen har avslutats', text: isOwner
        ? 'Öppna prenumerationsinställningarna för att välja hur företaget ska fortsätta.'
        : 'Företagets ägare väljer hur företaget ska fortsätta.' };
    if (code === 'TRIAL_EXPIRED' || entitlements.status === 'paused') return { tone: 'attention', title: 'Provperioden har tagit slut', text: isOwner
        ? 'Inget betalsätt registrerades innan provperioden tog slut. Lägg till ett kort eller välj faktura så öppnas företaget igen.'
        : 'Företagets ägare behöver välja betalsätt. Företaget öppnas så snart det är gjort.' };
    return { tone: 'attention', title: 'Betalning behövs för att fortsätta', text: isOwner
        ? 'Betala den utestående fakturan eller kontrollera betalsättet i prenumerationsinställningarna. Åtkomsten öppnas när betalningen har registrerats.'
        : 'Företagets ägare behöver betala den utestående fakturan. Företaget öppnas så snart betalningen har registrerats.' };
}

export default function BillingAccessRequired({ companyId, entitlements, onRefresh }) {
    const { capabilities } = useCapabilities();
    const isOwner = capabilities?.isOwner === true && capabilities.companyId === companyId;
    const [checking, setChecking] = useState(false);
    const [notice, setNotice] = useState('');
    const { tone, title, text } = explain(entitlements, isOwner);
    const code = entitlements.billingAccess?.code;
    // Restored access unmounts this card, so a message is only needed when it did not.
    const check = async () => {
        setChecking(true); setNotice('');
        try { await onRefresh?.(); setNotice('Åtkomsten är fortfarande pausad.'); }
        catch (_) { setNotice('Kunde inte hämta statusen. Försök igen.'); }
        finally { setChecking(false); }
    };
    const settings = <Link className={isOwner ? 'btn btn-primary billing-access-action' : 'btn btn-link billing-access-action'} to={`/${companyId}/settings/subscription`}>Öppna prenumerationen</Link>;
    const recheck = <button type="button" className={isOwner ? 'btn btn-link billing-access-action' : 'btn btn-primary billing-access-action'} disabled={checking} onClick={check}>{checking ? 'Kontrollerar…' : 'Kontrollera åtkomsten igen'}</button>;
    return <section className="modern-card billing-access-card" aria-labelledby="billing-access-title">
        <span className={`billing-access-icon billing-access-icon--${tone}`} aria-hidden="true"><LockKeyhole size={24} /></span>
        <h1 id="billing-access-title">{title}</h1>
        <p>{text}</p>
        <div className="billing-access-actions">
            {isOwner ? <>{settings}{recheck}</> : <>{recheck}{settings}</>}
            {code === 'BILLING_IDENTITY_REQUIRED' && <Link className="btn btn-link billing-access-action" to={`/${companyId}/settings/company`}>Öppna företagsuppgifter</Link>}
        </div>
        {notice && <p role="status" className="billing-access-note">{notice}</p>}
    </section>;
}
